import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import auth0 from 'auth0-js';
import type { Auth0DecodedHash, Auth0Error, WebAuth } from 'auth0-js';

const auth0Domain = process.env.REACT_APP_AUTH0_DOMAIN?.trim() ?? '';
const auth0ClientId = process.env.REACT_APP_AUTH0_CLIENT_ID?.trim() ?? '';
const auth0Audience = process.env.REACT_APP_AUTH0_AUDIENCE?.trim() ?? '';
const authStorageKey = 'returnem-auth-session';
const authScope = 'openid profile email';
const authRedirectPath = '/returns';
const tokenExpirySkewMs = 30_000;

interface AuthSession {
  accessToken: string;
  idToken: string;
  expiresAt: number;
  userEmail: string;
}

export interface ReturnemAuthContextValue {
  isConfigured: boolean;
  isAuthenticated: boolean;
  isLoading: boolean;
  userEmail: string;
  startEmailLogin: (email: string) => Promise<void>;
  verifyEmailCode: (email: string, code: string) => Promise<void>;
  logout: () => Promise<void>;
  getAccessToken: () => Promise<string>;
}

const unavailableAuth: ReturnemAuthContextValue = {
  isConfigured: false,
  isAuthenticated: false,
  isLoading: false,
  userEmail: '',
  startEmailLogin: async () => {
    throw new Error("Sign in isn't available right now.");
  },
  verifyEmailCode: async () => {
    throw new Error("Sign in isn't available right now.");
  },
  logout: async () => undefined,
  getAccessToken: async () => {
    throw new Error("Sign in isn't available right now.");
  },
};

const ReturnemAuthContext = createContext<ReturnemAuthContextValue>(unavailableAuth);

function getAuthRedirectUri(): string {
  return `${window.location.origin}${authRedirectPath}`;
}

function navigateToAuthRedirectPath(): void {
  window.history.replaceState({}, document.title, authRedirectPath);
  window.dispatchEvent(new Event('popstate'));
}

function createAuthClient(): WebAuth | null {
  if (!auth0Domain || !auth0ClientId || !auth0Audience) {
    return null;
  }

  return new auth0.WebAuth({
    domain: auth0Domain,
    clientID: auth0ClientId,
    redirectUri: getAuthRedirectUri(),
    responseType: 'token id_token',
    scope: authScope,
    audience: auth0Audience,
  });
}

function isAuthCallbackHash(): boolean {
  const hash = window.location.hash;
  return hash.includes('access_token=') || hash.includes('id_token=') || hash.includes('error=');
}

function clearUrlHash(): void {
  if (!window.location.hash) {
    return;
  }

  window.history.replaceState({}, document.title, `${window.location.pathname}${window.location.search}`);
}

function formatAuth0Error(error: Auth0Error): string {
  return error.errorDescription ?? error.error_description ?? error.description ?? "We couldn't sign you in. Please try again.";
}

function decodeJwtPayload(token: string): Record<string, unknown> | null {
  const payload = token.split('.')[1];

  if (!payload) {
    return null;
  }

  try {
    let normalizedPayload = payload.replace(/-/g, '+').replace(/_/g, '/');
    while (normalizedPayload.length % 4) {
      normalizedPayload += '=';
    }

    return JSON.parse(window.atob(normalizedPayload)) as Record<string, unknown>;
  } catch {
    return null;
  }
}

function readStoredSession(): AuthSession | null {
  try {
    const stored = window.localStorage.getItem(authStorageKey);
    const session = stored ? (JSON.parse(stored) as Partial<AuthSession>) : null;

    if (
      !session?.accessToken ||
      !session.idToken ||
      typeof session.expiresAt !== 'number' ||
      session.expiresAt <= Date.now()
    ) {
      window.localStorage.removeItem(authStorageKey);
      return null;
    }

    return {
      accessToken: session.accessToken,
      idToken: session.idToken,
      expiresAt: session.expiresAt,
      userEmail: typeof session.userEmail === 'string' ? session.userEmail : '',
    };
  } catch {
    window.localStorage.removeItem(authStorageKey);
    return null;
  }
}

function persistSession(authResult: Auth0DecodedHash): AuthSession {
  if (!authResult.accessToken || !authResult.idToken) {
    throw new Error("We couldn't finish sign in. Please try again.");
  }

  const idTokenPayload = (authResult.idTokenPayload as Record<string, unknown> | undefined) ?? decodeJwtPayload(authResult.idToken);
  const email = typeof idTokenPayload?.email === 'string' ? idTokenPayload.email : '';
  const expiresIn = typeof authResult.expiresIn === 'number' ? authResult.expiresIn : 3600;
  const session = {
    accessToken: authResult.accessToken,
    idToken: authResult.idToken,
    expiresAt: Date.now() + expiresIn * 1000 - tokenExpirySkewMs,
    userEmail: email,
  };

  window.localStorage.setItem(authStorageKey, JSON.stringify(session));
  return session;
}

export function ReturnemAuthProvider({ children }: { children: React.ReactNode }): React.ReactElement {
  const authClient = useMemo(() => createAuthClient(), []);
  const [session, setSession] = useState<AuthSession | null>(() => readStoredSession());
  const [isLoading, setIsLoading] = useState(() => Boolean(authClient && isAuthCallbackHash()));

  useEffect(() => {
    if (!authClient || !isAuthCallbackHash()) {
      setIsLoading(false);
      return;
    }

    authClient.parseHash((error, authResult) => {
      if (error) {
        clearUrlHash();
        setIsLoading(false);
        return;
      }

      try {
        const nextSession = authResult ? persistSession(authResult) : null;
        setSession(nextSession);
        navigateToAuthRedirectPath();
      } catch {
        clearUrlHash();
      } finally {
        setIsLoading(false);
      }
    });
  }, [authClient]);

  const value = useMemo<ReturnemAuthContextValue>(
    () => ({
      isConfigured: Boolean(authClient),
      isAuthenticated: Boolean(session),
      isLoading,
      userEmail: session?.userEmail ?? '',
      startEmailLogin: (email: string) =>
        new Promise<void>((resolve, reject) => {
          if (!authClient) {
            reject(new Error("Sign in isn't available right now."));
            return;
          }

          authClient.passwordlessStart(
            {
              connection: 'email',
              send: 'code',
              email,
              authParams: {
                audience: auth0Audience,
                scope: authScope,
                responseType: 'token id_token',
                redirectUri: getAuthRedirectUri(),
              },
            },
            (error) => {
              if (error) {
                reject(new Error(formatAuth0Error(error)));
                return;
              }

              resolve();
            },
          );
        }),
      verifyEmailCode: (email: string, code: string) =>
        new Promise<void>((resolve, reject) => {
          if (!authClient) {
            reject(new Error("Sign in isn't available right now."));
            return;
          }

          authClient.passwordlessLogin(
            {
              connection: 'email',
              email,
              verificationCode: code,
              audience: auth0Audience,
              scope: authScope,
              responseType: 'token id_token',
              redirectUri: getAuthRedirectUri(),
            },
            (error) => {
              if (error) {
                reject(new Error(formatAuth0Error(error)));
                return;
              }

              resolve();
            },
          );
        }),
      logout: async () => {
        window.localStorage.removeItem(authStorageKey);
        setSession(null);

        if (authClient) {
          authClient.logout({
            clientID: auth0ClientId,
            returnTo: window.location.origin,
          });
        }
      },
      getAccessToken: async () => {
        const currentSession = readStoredSession();

        if (!currentSession) {
          setSession(null);
          throw new Error('Please sign in again.');
        }

        return currentSession.accessToken;
      },
    }),
    [authClient, isLoading, session],
  );

  return <ReturnemAuthContext.Provider value={value}>{children}</ReturnemAuthContext.Provider>;
}

export function useReturnemAuth(): ReturnemAuthContextValue {
  return useContext(ReturnemAuthContext);
}
